// The learner's progress, held in memory, persisted to this device on every
// change, and handed to sync.js to merge with the cloud copy when signed in.
import { emptyProgress, sanitizeProgress, mergeProgress, canonical, localDay } from '../../shared/progress.js';

const KEY = 'bg.progress.v1';
const LOCAL_KEY = 'bg.local.v1';

let doc = load();
const listeners = new Set();

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return sanitizeProgress(JSON.parse(raw));
  } catch {
    // Private mode or corrupted storage: start fresh rather than crash.
  }
  return emptyProgress();
}

let storageOk = true;
function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(doc));
    storageOk = true;
  } catch {
    storageOk = false;
  }
}

export function storageAvailable() {
  return storageOk;
}

export function getProgress() {
  return doc;
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(reason) {
  for (const fn of listeners) {
    try {
      fn(doc, reason);
    } catch (e) {
      console.error(e);
    }
  }
}

function commit(reason, mutate) {
  const next = structuredClone(doc);
  mutate(next, Date.now());
  if (canonical(next) === canonical(doc)) return;
  doc = next;
  persist();
  emit(reason);
}

// Replaces the document with one merged from the server (sync.js).
export function adoptMerged(remote) {
  const merged = mergeProgress(doc, sanitizeProgress(remote));
  if (canonical(merged) === canonical(doc)) return false;
  doc = merged;
  persist();
  emit('sync');
  return true;
}

// Used by import and reset. Import merges, so importing never loses anything.
export function importDoc(input) {
  const clean = sanitizeProgress(input);
  doc = mergeProgress(doc, clean);
  persist();
  emit('import');
}

// Start over. Keeps settings and name; the reset timestamp makes the reset
// win over older data from other devices when they next sync.
export function resetAll() {
  doc = { ...emptyProgress(), resetAt: Date.now(), settings: doc.settings, profile: doc.profile };
  persist();
  emit('reset');
}

// Replacing wholesale is only for "sign out and clear this device".
export function clearLocal() {
  doc = emptyProgress();
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  emit('reset');
}

function touchDay(d) {
  const day = localDay();
  d.days[day] = (d.days[day] || 0) + 1;
}

export const actions = {
  markVerse(id) {
    if (doc.verses[id]) return;
    commit('verse', (d, t) => {
      d.verses[id] = { t };
      touchDay(d);
    });
  },
  markVerses(ids) {
    const fresh = ids.filter((id) => !doc.verses[id]);
    if (!fresh.length) return;
    commit('verse', (d, t) => {
      for (const id of fresh) d.verses[id] = { t };
      touchDay(d);
    });
  },
  completeLesson(id, verseIds = []) {
    commit('lesson', (d, t) => {
      if (!d.lessons[id]) d.lessons[id] = { t };
      for (const v of verseIds) if (!d.verses[v]) d.verses[v] = { t };
      touchDay(d);
    });
  },
  recordQuiz(lessonId, correct, total) {
    commit('quiz', (d, t) => {
      d.quiz[lessonId] = { t, correct, total };
    });
  },
  toggleBookmark(verseId) {
    const on = !(doc.bookmarks[verseId] && doc.bookmarks[verseId].on);
    commit('bookmark', (d, t) => {
      d.bookmarks[verseId] = { t, on };
    });
    return on;
  },
  setNote(key, text) {
    const clean = String(text || '').slice(0, 8000);
    const prev = doc.notes[key];
    if ((prev ? prev.text : '') === clean) return;
    commit('note', (d, t) => {
      d.notes[key] = { t, text: clean };
    });
  },
  setSettings(patch) {
    commit('settings', (d, t) => {
      d.settings = { ...defaultSettings(), ...(d.settings || {}), ...patch, t };
    });
  },
  setCurrent(lesson, step) {
    const c = doc.current;
    if (c && c.lesson === lesson && c.step === step) return;
    commit('current', (d, t) => {
      d.current = { t, lesson, step };
    });
  },
  setName(name) {
    commit('profile', (d, t) => {
      d.profile = { t, name: String(name || '').trim().slice(0, 40) };
    });
  },
};

export function defaultSettings() {
  return { theme: 'system', textSize: 'm', sanskrit: false, iast: true, meaning: true };
}

export function settings() {
  return { ...defaultSettings(), ...(doc.settings || {}) };
}

export function isBookmarked(verseId) {
  return !!(doc.bookmarks[verseId] && doc.bookmarks[verseId].on);
}

export function noteFor(key) {
  const n = doc.notes[key];
  return n && n.text ? n : null;
}

export function hasAnyProgress() {
  return Object.keys(doc.lessons).length > 0 || Object.keys(doc.verses).length > 0 || !!doc.current;
}

// Device-only state (never synced): hints dismissed, last sync time.
export function local() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
  } catch {
    return {};
  }
}

export function setLocal(patch) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify({ ...local(), ...patch }));
  } catch {
    // ignore
  }
}
